import { useState } from "react";
import type { ChildProfile } from "@howcomet/shared";
import { AVATARS } from "@howcomet/shared";
import { api, ApiError } from "../api";

/**
 * What a child sees between signing up and their grown-up finishing setup.
 *
 * The app is genuinely closed here, not merely hidden: the server refuses every
 * route that would collect or return a child's data until a parent has
 * consented. Under-13 means consent comes first, so a question asked before
 * then is a question we had no right to store. This screen is the courtesy;
 * `requireConsentedParent` is the control.
 *
 * It is deliberately not framed as a punishment. Nothing has gone wrong — a
 * grown-up just hasn't opened their email yet.
 */
export function WaitingForParentScreen({
  profile,
  onReady,
  onSignOut,
}: {
  profile: ChildProfile;
  onReady: () => void;
  onSignOut: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState(profile.parentEmail);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const avatar = AVATARS.find((a) => a.id === profile.avatarId)?.emoji ?? "⭐";

  const send = async (changed: boolean) => {
    if (busy) return;
    setBusy(true);
    setError("");
    setNote("");
    try {
      const result = await api.setParentEmail(changed ? email.trim().toLowerCase() : undefined);
      setEditing(false);
      if (result.parentReady) {
        // The corrected address already belongs to a set-up grown-up, so the
        // gate is already open — no waiting required.
        onReady();
        return;
      }
      setNote(`Sent to ${result.parentEmail}. Ask them to check their email.`);
    } catch (e) {
      if (e instanceof ApiError && e.code === "already_set_up") {
        onReady();
        return;
      }
      setError(e instanceof ApiError ? e.message : "Couldn't send that.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rise safe-top mx-auto max-w-md px-6 pb-16 pt-10">
      <div className="text-center">
        <div className="text-[56px]">{avatar}</div>
        <h1 className="disp mt-2 text-[30px] leading-tight text-cream">
          Almost there, {profile.name}
        </h1>
        <p className="mt-3 text-[18px] leading-relaxed text-cream/70">
          A grown-up needs to finish setting up before you can start asking questions.
        </p>
      </div>

      <div className="mt-8 rounded-3xl bg-panel p-5">
        <p className="disp text-[14px] uppercase tracking-wider text-cream/45">
          We sent an email to
        </p>

        {!editing ? (
          <>
            <p className="mt-1 break-all text-[18px] text-cream">{profile.parentEmail}</p>
            <p className="mt-3 text-[15px] leading-relaxed text-cream/60">
              They open the link, make a password, and then you're in.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => void send(false)}
                disabled={busy}
                className="kidbtn rounded-full bg-cream/12 px-5 py-2 font-bold text-cream disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send it again"}
              </button>
              <button
                onClick={() => setEditing(true)}
                className="kidbtn rounded-full bg-cream/12 px-5 py-2 font-bold text-cream"
              >
                That's the wrong address
              </button>
            </div>
          </>
        ) : (
          <>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              autoFocus
              className="mt-2 w-full rounded-2xl bg-panel2 px-4 py-3 text-[17px] text-cream outline-none"
            />
            <p className="mt-2 text-[14px] leading-relaxed text-cream/50">
              Check the spelling with a grown-up. We'll send the email again to the new address.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => void send(true)}
                disabled={!/.+@.+\..+/.test(email) || busy}
                className="kidbtn disp rounded-full bg-gold px-5 py-2 text-night disabled:bg-cream/15 disabled:text-cream/40"
              >
                {busy ? "Sending…" : "Send to this one"}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setEmail(profile.parentEmail);
                }}
                className="kidbtn rounded-full bg-cream/12 px-5 py-2 font-bold text-cream"
              >
                Never mind
              </button>
            </div>
          </>
        )}

        {note && <p className="mt-3 text-[15px] font-bold text-mint">{note}</p>}
        {error && <p className="mt-3 text-[15px] text-amber">{error}</p>}
      </div>

      <button
        onClick={onReady}
        className="kidbtn disp mt-6 w-full rounded-3xl bg-gold py-4 text-[19px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)]"
      >
        They've done it — let me in
      </button>

      <p className="mt-4 text-center text-[14px] leading-relaxed text-cream/45">
        Your sky name is <b className="text-cream/70">{profile.handle}</b>. Keep it somewhere safe.
      </p>

      <button onClick={onSignOut} className="kidbtn mt-4 w-full py-2 text-[14px] text-cream/40">
        Sign out
      </button>
    </div>
  );
}
